import React, { useEffect, useState } from 'react';
import { Form, Button, Input, Switch, Card, Tag, Row, Col, Modal, Tooltip, Select } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { ArrowForward, CheckCircleOutline, RadioButtonUnchecked } from '@material-ui/icons';
import toast from 'components/Basic/Toast';
import Loading from 'components/Basic/Loading';
import {
  useLazyGetIntegrationQuery,
  useUpdateIntegrationMutation,
} from 'core/integrations/IntegrationsService';

const { TextArea } = Input;
const { Option } = Select;

const SLUG = 'apns';

const DEFAULT_TEAM_ID = 'GM2E2T4KTN';
const KEY_ID_LENGTH = 10;

type ApnsEnvironment = 'sandbox' | 'production';

interface ApnsSettings {
  teamId: string;
  bundleId: string;
  keyId: string;
  environment: ApnsEnvironment;
}

const DEFAULT_SETTINGS: ApnsSettings = {
  teamId: DEFAULT_TEAM_ID,
  bundleId: '',
  keyId: '',
  environment: 'sandbox',
};

// The .p8 never comes back from the API: the GET returns '***' when it is stored and '' when not.
const isP8Stored = (value: unknown): boolean => typeof value === 'string' && value.trim() !== '';

// The backend takes the PEM or its base64. The PEM has line breaks, so it travels base64-encoded.
const encodeP8 = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed.includes('-----BEGIN')) return trimmed;
  return window.btoa(trimmed);
};

const errorMessage = (error: any): string | null => {
  const message = error?.data?.message;
  if (Array.isArray(message)) return message.join(' ');
  return typeof message === 'string' && message !== '' ? message : null;
};

const ApnsConfig: React.FC = () => {
  const intl = useIntl();

  const [settings, setSettings] = useState<ApnsSettings>(DEFAULT_SETTINGS);
  const [initialSettings, setInitialSettings] = useState<ApnsSettings>(DEFAULT_SETTINGS);
  const [p8Stored, setP8Stored] = useState(false);
  const [newP8, setNewP8] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [initialEnabled, setInitialEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [getIntegration] = useLazyGetIntegrationQuery();
  const [updateIntegration] = useUpdateIntegrationMutation();

  useEffect(() => {
    setIsLoading(true);
    getIntegration({ slug: SLUG })
      .unwrap()
      .then(res => {
        const s = res.settings ?? {};
        const loaded: ApnsSettings = {
          teamId: s.teamId ?? DEFAULT_TEAM_ID,
          bundleId: s.bundleId ?? '',
          keyId: s.keyId ?? '',
          environment: s.environment === 'production' ? 'production' : 'sandbox',
        };
        setSettings(loaded);
        setInitialSettings(loaded);
        setP8Stored(isP8Stored(s.p8));
        setEnabled(res.enabled);
        setInitialEnabled(res.enabled);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [getIntegration]);

  const handleChange = <K extends keyof ApnsSettings>(field: K, value: ApnsSettings[K]) =>
    setSettings(prev => ({ ...prev, [field]: value }));

  const hasP8 = p8Stored || newP8.trim() !== '';
  const isComplete =
    settings.teamId.trim() !== '' && settings.bundleId.trim() !== '' && settings.keyId.trim() !== '' && hasP8;

  const onCancel = () => {
    setSettings(initialSettings);
    setEnabled(initialEnabled);
    setNewP8('');
  };

  const doSave = async () => {
    if (settings.keyId.trim() !== '' && settings.keyId.trim().length !== KEY_ID_LENGTH) {
      toast.error({ title: intl.formatMessage({ id: 'Key ID must be exactly 10 characters.' }) });
      return;
    }

    let p8Payload: string | null = null;
    if (newP8.trim() !== '') {
      try {
        p8Payload = encodeP8(newP8);
      } catch {
        toast.error({ title: intl.formatMessage({ id: 'The Auth Key is not a valid private key.' }) });
        return;
      }
    }

    setIsLoading(true);
    try {
      await updateIntegration({
        slug: SLUG,
        body: {
          enabled,
          settings: {
            teamId: settings.teamId.trim(),
            bundleId: settings.bundleId.trim(),
            keyId: settings.keyId.trim(),
            environment: settings.environment,
            // Omitted when the user did not type a new one: an empty string deletes the stored key.
            ...(p8Payload ? { p8: p8Payload } : {}),
          },
        },
      }).unwrap();
      setInitialSettings(settings);
      setInitialEnabled(enabled);
      if (p8Payload) setP8Stored(true);
      setNewP8('');
      toast.success({ title: intl.formatMessage({ id: 'APNs configuration updated successfully!' }) });
    } catch (error) {
      toast.error({
        title: errorMessage(error) ?? intl.formatMessage({ id: 'Error updating APNs configuration' }),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onSave = () => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'Save changes' }),
      content: intl.formatMessage({ id: 'Save integration warning' }),
      okText: intl.formatMessage({ id: 'Save' }),
      cancelText: intl.formatMessage({ id: 'Cancel' }),
      className: 'integration_confirm_modal',
      onOk: doSave,
    });
  };

  const statusCardStyle = {
    borderColor: enabled ? '#b7eb8f' : '#ffccc7',
    background: enabled ? '#f6ffed' : '#fff2f0',
    borderRadius: 8,
  };

  const switchDisabled = !enabled && !isComplete;

  return (
    <div className="integrations_layout">
      <Loading visible={isLoading} />

      <div className="integrations_status_card">
        <Card style={statusCardStyle}>
          <div className="status_row">
            <div className="status_info">
              <p className="title" style={{ marginBottom: 4 }}>
                <FormattedMessage id="Enable APNs Integration" />
              </p>
              <div className="status_badge">
                {enabled
                  ? <CheckCircleOutline style={{ fontSize: 15, color: '#52c41a' }} />
                  : <RadioButtonUnchecked style={{ fontSize: 15, color: '#ff4d4f' }} />
                }
                <Tag color={enabled ? 'success' : 'error'} style={{ margin: 0 }}>
                  {enabled
                    ? <FormattedMessage id="Active" />
                    : <FormattedMessage id="Inactive" />
                  }
                </Tag>
              </div>
              {switchDisabled && (
                <p className="description" style={{ marginBottom: 0, color: '#faad14' }}>
                  <FormattedMessage id="Complete Team ID, Bundle ID, Key ID and the Auth Key to enable the integration." />
                </p>
              )}
            </div>
            <Tooltip
              title={
                switchDisabled
                  ? intl.formatMessage({ id: 'Complete Team ID, Bundle ID, Key ID and the Auth Key to enable the integration.' })
                  : ''
              }
            >
              <span>
                <Switch
                  id="integration-apns-enabled"
                  checked={enabled}
                  disabled={switchDisabled}
                  onChange={setEnabled}
                />
              </span>
            </Tooltip>
          </div>
        </Card>
      </div>

      <Card
        title={<FormattedMessage id="APNs Credentials" />}
        size="small"
        style={{ borderRadius: 8 }}
      >
        <Form layout="vertical" colon={false}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={<span className="title" style={{ marginBottom: 0 }}><FormattedMessage id="Team ID" /></span>}
                extra={<FormattedMessage id="Apple developer team identifier. The same one for every region." />}
                style={{ marginBottom: 14 }}
              >
                <Input
                  id="integration-apns-team-id"
                  autoComplete="off"
                  spellCheck={false}
                  value={settings.teamId}
                  placeholder={DEFAULT_TEAM_ID}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('teamId', e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={<span className="title" style={{ marginBottom: 0 }}><FormattedMessage id="Key ID" /></span>}
                extra={<FormattedMessage id="10 characters, taken from the file name AuthKey_XXXXXXXXXX.p8" />}
                style={{ marginBottom: 14 }}
              >
                <Input
                  id="integration-apns-key-id"
                  autoComplete="off"
                  spellCheck={false}
                  maxLength={KEY_ID_LENGTH}
                  value={settings.keyId}
                  placeholder="ABCD123XYZ"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('keyId', e.target.value)}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={<span className="title" style={{ marginBottom: 0 }}><FormattedMessage id="Bundle ID" /></span>}
                extra={<FormattedMessage id="Bundle id of the iOS build for this environment." />}
                style={{ marginBottom: 14 }}
              >
                <Input
                  id="integration-apns-bundle-id"
                  autoComplete="off"
                  spellCheck={false}
                  value={settings.bundleId}
                  placeholder="com.mcdonalds.example"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('bundleId', e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={<span className="title" style={{ marginBottom: 0 }}><FormattedMessage id="Apple server" /></span>}
                extra={<FormattedMessage id="Production for App Store, TestFlight and ad hoc builds. Sandbox for builds signed with a development profile." />}
                style={{ marginBottom: 14 }}
              >
                <Select
                  id="integration-apns-environment"
                  style={{ width: '100%' }}
                  value={settings.environment}
                  onChange={(value: ApnsEnvironment) => handleChange('environment', value)}
                  getPopupContainer={trigger => trigger.parentNode}
                >
                  <Option value="production"><FormattedMessage id="Production" /></Option>
                  <Option value="sandbox"><FormattedMessage id="Sandbox" /></Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={
              <span className="title" style={{ marginBottom: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <FormattedMessage id="Auth Key (.p8)" />
                <Tag color={p8Stored ? 'success' : 'default'} style={{ margin: 0 }}>
                  {p8Stored
                    ? <FormattedMessage id="Auth Key configured" />
                    : <FormattedMessage id="Auth Key not configured" />
                  }
                </Tag>
              </span>
            }
            extra={
              p8Stored
                ? <FormattedMessage id="The stored key cannot be displayed. Paste a new one only to replace it; leave it empty to keep the current one." />
                : <FormattedMessage id="Paste the contents of the AuthKey_XXXXXXXXXX.p8 file. It is stored encrypted and cannot be read back." />
            }
            style={{ marginBottom: 14 }}
          >
            <TextArea
              id="integration-apns-p8"
              autoComplete="off"
              spellCheck={false}
              rows={6}
              value={newP8}
              placeholder="-----BEGIN PRIVATE KEY-----"
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNewP8(e.target.value)}
            />
          </Form.Item>
        </Form>
      </Card>

      <div className="integrations_actions">
        <Button id="integration-apns-save" onClick={onSave}>
          <FormattedMessage id="Save" />
          <ArrowForward />
        </Button>
        <p id="integration-apns-cancel" className="integrations_cancel_btn" onClick={onCancel}>
          <FormattedMessage id="Cancel" />
        </p>
      </div>

    </div>
  );
};

export default ApnsConfig;
